/**
 * Strap Combo Builder - Flexible Version
 * Supports: Strap only, Pad only, or Combo purchase
 */

class StrapComboBuilder {
  constructor() {
    this.strapSelect = document.querySelector('[data-strap-select]');
    this.padSelect = document.querySelector('[data-pad-select]');
    this.addButton = document.querySelector('[data-add-combo]');
    this.messageContainer = document.querySelector('[data-message]');
    this.previewStrap = document.querySelector('[data-preview-strap]');
    this.previewPad = document.querySelector('[data-preview-pad]');
    this.priceDisplay = document.querySelector('[data-dynamic-price]');
    
    if (!this.strapSelect || !this.padSelect || !this.addButton) {
      console.error('Strap Combo Builder: Required elements not found');
      return;
    }

    this.init();
  }

  init() {
    this.strapSelect.addEventListener('change', () => this.updateState());
    this.padSelect.addEventListener('change', () => this.updateState());
    this.addButton.addEventListener('click', () => this.addToCart());
    this.updateState();
  }

  updateState() {
    const strapColor = this.strapSelect.value;
    const padColor = this.padSelect.value;

    // Update visual previews
    if (this.previewStrap && strapColor) {
      this.previewStrap.setAttribute('data-color', strapColor);
      this.previewStrap.closest('.preview-item').classList.add('selected');
    } else if (this.previewStrap) {
      this.previewStrap.closest('.preview-item').classList.remove('selected');
    }
    
    if (this.previewPad && padColor) {
      this.previewPad.setAttribute('data-color', padColor);
      this.previewPad.closest('.preview-item').classList.add('selected');
    } else if (this.previewPad) {
      this.previewPad.closest('.preview-item').classList.remove('selected');
    }

    // Update button state and text
    const hasStrap = !!strapColor;
    const hasPad = !!padColor;
    const isCombo = hasStrap && hasPad;
    
    if (!hasStrap && !hasPad) {
      this.addButton.disabled = true;
      this.addButton.querySelector('.button-text').textContent = 'Select Color(s)';
    } else if (isCombo) {
      this.addButton.disabled = false;
      this.addButton.querySelector('.button-text').textContent = 'Add Combo to Cart';
    } else if (hasStrap) {
      this.addButton.disabled = false;
      this.addButton.querySelector('.button-text').textContent = 'Add Strap to Cart';
    } else {
      this.addButton.disabled = false;
      this.addButton.querySelector('.button-text').textContent = 'Add Pad to Cart';
    }

    // Update price display
    this.updatePriceDisplay(hasStrap, hasPad, isCombo);
  }

  updatePriceDisplay(hasStrap, hasPad, isCombo) {
    if (!this.priceDisplay) return;

    // Get prices from data attributes
    const strapPrice = parseFloat(this.priceDisplay.dataset.strapPrice || 24.99);
    const padRegularPrice = parseFloat(this.priceDisplay.dataset.padRegular || 7.99);
    const padComboPrice = parseFloat(this.priceDisplay.dataset.padCombo || 0.99);

    let html = '';
    let total = 0;

    if (hasStrap && hasPad) {
      // Combo pricing
      html = `
        <div class="price-row">
          <span>Shoulder Strap</span>
          <span>$${strapPrice.toFixed(2)}</span>
        </div>
        <div class="price-row highlight">
          <span>Matching Pad</span>
          <span>
            <span class="crossed">$${padRegularPrice.toFixed(2)}</span>
            <span class="deal">$${padComboPrice.toFixed(2)}</span>
          </span>
        </div>
        <div class="price-divider"></div>
        <div class="price-total">
          <span>Total</span>
          <span>$${(strapPrice + padComboPrice).toFixed(2)}</span>
        </div>
        <div class="savings-badge">Save $${(padRegularPrice - padComboPrice).toFixed(2)}!</div>
      `;
    } else if (hasStrap) {
      // Strap only
      html = `
        <div class="price-row">
          <span>Shoulder Strap</span>
          <span>$${strapPrice.toFixed(2)}</span>
        </div>
        <div class="price-total">
          <span>Total</span>
          <span>$${strapPrice.toFixed(2)}</span>
        </div>
        <div class="suggestion">💡 Add matching pad for just $${padComboPrice.toFixed(2)}</div>
      `;
    } else if (hasPad) {
      // Pad only
      html = `
        <div class="price-row">
          <span>Shoulder Strap Pad</span>
          <span>$${padRegularPrice.toFixed(2)}</span>
        </div>
        <div class="price-total">
          <span>Total</span>
          <span>$${padRegularPrice.toFixed(2)}</span>
        </div>
        <div class="suggestion">💡 Bundle with strap for only $${padComboPrice.toFixed(2)}</div>
      `;
    }

    this.priceDisplay.innerHTML = html;
  }

  getSelectedVariantId(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    return selectedOption ? selectedOption.getAttribute('data-variant-id') : null;
  }

  showMessage(message, type = 'success') {
    this.messageContainer.textContent = message;
    this.messageContainer.className = `combo-message ${type}`;
    this.messageContainer.style.display = 'block';
    
    if (type === 'success') {
      setTimeout(() => {
        this.messageContainer.style.display = 'none';
      }, 3000);
    }
  }

  setButtonLoading(isLoading) {
    const buttonText = this.addButton.querySelector('.button-text');
    const buttonLoading = this.addButton.querySelector('.button-loading');
    
    if (isLoading) {
      buttonText.style.display = 'none';
      buttonLoading.style.display = 'inline';
      this.addButton.disabled = true;
    } else {
      buttonText.style.display = 'inline';
      buttonLoading.style.display = 'none';
      this.updateState();
    }
  }

  async addToCart() {
    const strapVariantId = this.getSelectedVariantId(this.strapSelect);
    const padVariantId = this.getSelectedVariantId(this.padSelect);

    const items = [];
    
    if (strapVariantId) {
      items.push({ id: strapVariantId, quantity: 1 });
    }
    
    if (padVariantId) {
      items.push({ id: padVariantId, quantity: 1 });
    }

    if (items.length === 0) {
      this.showMessage('Please select at least one item', 'error');
      return;
    }

    this.setButtonLoading(true);
    this.messageContainer.style.display = 'none';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      const result = await response.json();

      // Success message based on what was added
      let message = '✓ Added to cart!';
      if (items.length === 2) {
        message = '✓ Combo added! Bundle discount will apply automatically.';
      }
      
      this.showMessage(message, 'success');
      
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      let errorMessage = 'Sorry, there was an error. Please try again.';
      
      if (error.message.includes('sold out') || error.message.includes('available')) {
        errorMessage = 'Selected item(s) are currently out of stock.';
      }
      
      this.showMessage(errorMessage, 'error');
      this.setButtonLoading(false);
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StrapComboBuilder();
  });
} else {
  new StrapComboBuilder();
}